import React, { useEffect, useState } from "react"
import { ContactOpportunityStatus, ContactsFiltersConditions, ConditionOperator, FilterTypeToFilterData } from "@/protocols/ClientCatalogFilterProtocol"
import { FormControl, Grid, InputAdornment, MenuItem, Select, TextField } from "@material-ui/core"
import useDidMount from "@/hooks/useDidMount"
import BoardService, { BoardLossReason, BoardSummary } from "@/services/BoardService"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import { sanitizeMoneyInputValue, parseMoneyInputValueToCents, formatCentsToMoneyInputValue } from "@/utils/currencyFormatter"

type ContactBoardFilterConditionProps = {
	condition: ContactsFiltersConditions<"contact_board">
	onChange: (condition: ContactsFiltersConditions<"contact_board">) => void
}

type ContactBoardConditionKind =
	| "contact_in_stage"
	| "contact_not_in_stage"
	| "contact_value"
	| "contact_status"
	| "contact_loss_reason"

type ContactBoardOption = {
	operator: ConditionOperator
	label: string
}

const contactBoardSlugToContactBoardOptions: Record<ContactBoardConditionKind, ContactBoardOption> = {
	contact_in_stage: {
		operator: "in",
		label: "Contato pertence à etapa"
	},
	contact_not_in_stage: {
		operator: "not_in",
		label: "Contato não pertence à etapa"
	},
	contact_value: {
		operator: "less_than",
		label: "Valor do contato"
	},
	contact_status: {
		operator: "equal",
		label: "Status do contato"
	},
	contact_loss_reason: {
		operator: "equal",
		label: "Motivo da perda"
	}
}

type ContactValueOperator = Extract<ConditionOperator, "less_than" | "greater_than" | "between" | "equal">

const contactValueOperatorToLabel: Record<ContactValueOperator, string> = {
	less_than: "Menor que",
	greater_than: "Maior que",
	between: "Entre",
	equal: "Igual"
}

const contactValueOperators = Object.keys(contactValueOperatorToLabel) as ContactValueOperator[]

type ContactStatusOperator = Extract<ConditionOperator, "equal" | "not_equal">

const contactStatusOperatorToLabel: Record<ContactStatusOperator, string> = {
	equal: "Igual",
	not_equal: "Não igual"
}

const contactStatusOperators = Object.keys(contactStatusOperatorToLabel) as ContactStatusOperator[]

const contactStatusValueToLabel: Record<ContactOpportunityStatus, string> = {
	won: "Ganho",
	lost: "Perdido"
}

const contactStatusValues = Object.keys(contactStatusValueToLabel) as ContactOpportunityStatus[]

/**
 * Sentinel value reserved for the "Outro" (Other) loss reason option that boards
 * expose via lossOtherEnabled. It does not correspond to any real board_loss_reasons.id.
 */
const LOSS_REASON_OTHER_ID = 0

const GRID_WIDTH_NARROW = 2
const GRID_WIDTH_DEFAULT = 3
const GRID_WIDTH_WIDE = 4
const GRID_WIDTH_WIDEST = 5

type ConditionKindResolver = {
	matches: (operator: ConditionOperator, filterData: FilterTypeToFilterData["contact_board"]) => boolean
	kind: ContactBoardConditionKind
}

const conditionKindResolvers: ConditionKindResolver[] = [
	{ matches: (_operator, filterData) => filterData.contact_opportunity_status !== undefined, kind: "contact_status" },
	{ matches: (_operator, filterData) => filterData.contact_loss_reason_id !== undefined, kind: "contact_loss_reason" },
	{ matches: (operator) => operator === "in", kind: "contact_in_stage" },
	{ matches: (operator) => operator === "not_in", kind: "contact_not_in_stage" }
]

const determineConditionKind = (
	operator: ConditionOperator,
	filterData: FilterTypeToFilterData["contact_board"]
): ContactBoardConditionKind => {
	const matchedResolver = conditionKindResolvers.find(resolver => resolver.matches(operator, filterData))

	return matchedResolver?.kind ?? "contact_value"
}

const ContactBoardFilterCondition: React.FC<ContactBoardFilterConditionProps> = ({
	condition,
	onChange
}) => {
	const [boards, setBoards] = useState<Array<BoardSummary>>([])
	const [fromValueInReals, setFromValueInReals] = useState<string>(
		formatCentsToMoneyInputValue(condition.filterData.contact_value_from_in_cents)
	)
	const [toValueInReals, setToValueInReals] = useState<string>(
		formatCentsToMoneyInputValue(condition.filterData.contact_value_to_in_cents)
	)

	const funnelSubscriptionData = useSubscriptionLimits("permission", "funnel")
	const canUseContactValue = !funnelSubscriptionData.mustBlock
	const canUseBoardOpportunityStatus = !funnelSubscriptionData.mustBlock

	const conditionKind = determineConditionKind(condition.operator, condition.filterData)
	const isValueMode = conditionKind === "contact_value"
	const isStatusMode = conditionKind === "contact_status"
	const isLossReasonMode = conditionKind === "contact_loss_reason"
	const isBetweenOperator = isValueMode && condition.operator === "between"

	const selectedBoard = boards.find(board => board.id === condition.filterData.contact_board_id)
	const selectedStatusValue = condition.filterData.contact_opportunity_status

	const buildLossReasonOptions = (board: BoardSummary | undefined): Array<BoardLossReason & { id: number }> => {
		const lossReasonsList = board?.lossReasons ?? []
		const configuredReasons = lossReasonsList.map(reason => ({ ...reason }))

		if (board?.lossOtherEnabled) {
			return [...configuredReasons, { id: LOSS_REASON_OTHER_ID, reasonText: "Outro" }]
		}

		return configuredReasons
	}

	const handleGetBoards = async () => {
		try {
			const instanceBoards = await BoardService.retrieveAllBoardsWithStagesNoCards()

			setBoards(instanceBoards)

			if (!condition.filterData.contact_board_id) {
				onChange({
					type: "contact_board",
					operator: "in",
					filterData: {
						contact_board_id: instanceBoards[0]?.id,
						contact_board_stage_id: instanceBoards[0]?.stages[0]?.id
					}
				})
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleChangeBoard = (boardId: number) => {
		onChange({
			...condition,
			filterData: {
				...condition.filterData,
				contact_board_id: boardId
			}
		})
	}

	const handleChangeStage = (stageId: number) => {
		onChange({
			...condition,
			filterData: {
				...condition.filterData,
				contact_board_stage_id: stageId
			}
		})
	}

	const handleChangeConditionKind = (kind: ContactBoardConditionKind) => {
		const { operator } = contactBoardSlugToContactBoardOptions[kind]

		const firstLossReasonId = buildLossReasonOptions(selectedBoard)[0]?.id ?? 0

		const conditionKindToFilterData: Record<ContactBoardConditionKind, FilterTypeToFilterData["contact_board"]> = {
			contact_in_stage: {
				contact_board_id: condition.filterData.contact_board_id,
				contact_board_stage_id: selectedBoard?.stages[0]?.id
			},
			contact_not_in_stage: {
				contact_board_id: condition.filterData.contact_board_id,
				contact_board_stage_id: selectedBoard?.stages[0]?.id
			},
			contact_value: {
				contact_board_id: condition.filterData.contact_board_id
			},
			contact_status: {
				contact_board_id: condition.filterData.contact_board_id,
				contact_opportunity_status: "won"
			},
			contact_loss_reason: {
				contact_board_id: condition.filterData.contact_board_id,
				contact_loss_reason_id: firstLossReasonId
			}
		}

		setFromValueInReals("")
		setToValueInReals("")

		onChange({
			type: "contact_board",
			operator,
			filterData: conditionKindToFilterData[kind]
		})
	}

	const handleChangeValueOperator = (operator: ContactValueOperator) => {
		const isChangingToBetween = operator === "between"

		onChange({
			...condition,
			operator,
			filterData: {
				contact_board_id: condition.filterData.contact_board_id,
				contact_value_from_in_cents: condition.filterData.contact_value_from_in_cents,
				contact_value_to_in_cents: isChangingToBetween ? condition.filterData.contact_value_to_in_cents : undefined
			}
		})
	}

	const handleChangeFromValue = (inputValue: string) => {
		const sanitizedValue = sanitizeMoneyInputValue(inputValue)

		setFromValueInReals(sanitizedValue)

		onChange({
			...condition,
			filterData: {
				...condition.filterData,
				contact_value_from_in_cents: parseMoneyInputValueToCents(sanitizedValue)
			}
		})
	}

	const handleChangeToValue = (inputValue: string) => {
		const sanitizedValue = sanitizeMoneyInputValue(inputValue)

		setToValueInReals(sanitizedValue)

		onChange({
			...condition,
			filterData: {
				...condition.filterData,
				contact_value_to_in_cents: parseMoneyInputValueToCents(sanitizedValue)
			}
		})
	}

	const handleChangeStatusOperator = (operator: ContactStatusOperator) => {
		onChange({
			...condition,
			operator
		})
	}

	const handleChangeStatusValue = (status: ContactOpportunityStatus) => {
		onChange({
			...condition,
			filterData: {
				contact_board_id: condition.filterData.contact_board_id,
				contact_opportunity_status: status
			}
		})
	}

	const handleChangeLossReason = (lossReasonId: number) => {
		onChange({
			...condition,
			filterData: {
				...condition.filterData,
				contact_loss_reason_id: lossReasonId
			}
		})
	}

	useDidMount(() => {
		handleGetBoards()
	})

	useEffect(() => {
		setFromValueInReals(formatCentsToMoneyInputValue(condition.filterData.contact_value_from_in_cents))
		setToValueInReals(formatCentsToMoneyInputValue(condition.filterData.contact_value_to_in_cents))
	}, [condition.filterData.contact_value_from_in_cents, condition.filterData.contact_value_to_in_cents])

	const selectedBoardId = condition.filterData.contact_board_id
	const selectedStageId = condition.filterData.contact_board_stage_id
	const selectedValueOperator = isValueMode ? condition.operator as ContactValueOperator : "less_than"
	const selectedStatusOperator = isStatusMode ? condition.operator as ContactStatusOperator : "equal"
	const selectedLossReasonId = condition.filterData.contact_loss_reason_id

	const availableConditionKinds = Object.keys(contactBoardSlugToContactBoardOptions) as ContactBoardConditionKind[]

	const conditionKindOptions = availableConditionKinds
		.filter(kind => kind !== "contact_value" || canUseContactValue)
		.filter(kind => kind !== "contact_status" || canUseBoardOpportunityStatus)
		.filter(kind => kind !== "contact_loss_reason" || canUseBoardOpportunityStatus)

	const getRowLayoutMode = (): "stage" | "valueBetween" | "valueSingle" | "status" | "lossReason" => {
		if (isLossReasonMode) {
			return "lossReason"
		}

		if (isStatusMode) {
			return "status"
		}

		if (!isValueMode) {
			return "stage"
		}

		if (isBetweenOperator) {
			return "valueBetween"
		}

		return "valueSingle"
	}

	const rowLayoutMode = getRowLayoutMode()

	const rowLayoutModeToBoardSelectWidth: Record<typeof rowLayoutMode, 2 | 3> = {
		stage: GRID_WIDTH_DEFAULT,
		valueSingle: GRID_WIDTH_DEFAULT,
		valueBetween: GRID_WIDTH_NARROW,
		status: GRID_WIDTH_DEFAULT,
		lossReason: GRID_WIDTH_DEFAULT
	}

	const rowLayoutModeToConditionKindSelectWidth: Record<typeof rowLayoutMode, 3 | 4 | 5> = {
		stage: GRID_WIDTH_WIDEST,
		valueSingle: GRID_WIDTH_WIDE,
		valueBetween: GRID_WIDTH_WIDE,
		status: GRID_WIDTH_DEFAULT,
		lossReason: GRID_WIDTH_DEFAULT
	}

	const renderStageFields = () => {
		return (
			<Grid item xs={4}>
				<FormControl fullWidth variant="outlined" size="small">
					<Select
						variant="outlined"
						value={selectedStageId}
						onChange={({ target }) => handleChangeStage(target.value as number)}
						fullWidth
					>
						{boards?.find(board => board.id === selectedBoardId)?.stages?.map((stage) => (
							<MenuItem key={stage.id} value={stage.id}>
								{stage.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
		)
	}

	const renderValueFields = () => {
		return (
			<>
				<Grid item xs={2}>
					<FormControl fullWidth variant="outlined" size="small">
						<Select
							variant="outlined"
							value={selectedValueOperator}
							onChange={event => handleChangeValueOperator(event.target.value as ContactValueOperator)}
							fullWidth
						>
							{contactValueOperators.map((operator) => (
								<MenuItem key={operator} value={operator}>
									{contactValueOperatorToLabel[operator]}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={isBetweenOperator ? 2 : 3}>
					<TextField
						value={fromValueInReals}
						onChange={(event) => handleChangeFromValue(event.target.value)}
						variant="outlined"
						size="small"
						type="text"
						inputMode="decimal"
						fullWidth
						placeholder={isBetweenOperator ? "Mínimo" : "Valor"}
						InputProps={{ startAdornment: (<InputAdornment position="start">R$</InputAdornment>) }}
					/>
				</Grid>
				{isBetweenOperator && (
					<Grid item xs={2}>
						<TextField
							value={toValueInReals}
							onChange={(event) => handleChangeToValue(event.target.value)}
							variant="outlined"
							size="small"
							type="text"
							inputMode="decimal"
							fullWidth
							placeholder="Máximo"
							InputProps={{ startAdornment: (<InputAdornment position="start">R$</InputAdornment>) }}
						/>
					</Grid>
				)}
			</>
		)
	}

	const renderStatusFields = () => {
		return (
			<>
				<Grid item xs={2}>
					<FormControl fullWidth variant="outlined" size="small">
						<Select
							variant="outlined"
							value={selectedStatusOperator}
							onChange={event => handleChangeStatusOperator(event.target.value as ContactStatusOperator)}
							fullWidth
						>
							{contactStatusOperators.map((operator) => (
								<MenuItem key={operator} value={operator}>
									{contactStatusOperatorToLabel[operator]}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<FormControl fullWidth variant="outlined" size="small">
						<Select
							variant="outlined"
							value={selectedStatusValue ?? "won"}
							onChange={event => handleChangeStatusValue(event.target.value as ContactOpportunityStatus)}
							fullWidth
						>
							{contactStatusValues.map((status) => (
								<MenuItem key={status} value={status}>
									{contactStatusValueToLabel[status]}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</>
		)
	}

	const renderLossReasonFields = () => {
		return (
			<Grid item xs={6}>
				<FormControl fullWidth variant="outlined" size="small">
					<Select
						variant="outlined"
						value={selectedLossReasonId ?? ""}
						onChange={({ target }) => handleChangeLossReason(target.value as number)}
						fullWidth
						displayEmpty
					>
						<MenuItem value="" disabled>
							Motivo da perda
						</MenuItem>
						{buildLossReasonOptions(selectedBoard).map((reason) => (
							<MenuItem key={reason.id} value={reason.id}>
								{reason.reasonText}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
		)
	}

	return (
		<Grid container direction="row" spacing={1}>
			<Grid item xs={rowLayoutModeToBoardSelectWidth[rowLayoutMode]}>
				<FormControl fullWidth variant="outlined" size="small">
					<Select
						variant="outlined"
						value={selectedBoardId}
						onChange={({ target }) => handleChangeBoard(target.value as number)}
						fullWidth
						displayEmpty
					>
						{boards?.map((board) => (
							<MenuItem key={board.id} value={board.id}>
								{board.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={rowLayoutModeToConditionKindSelectWidth[rowLayoutMode]}>
				<FormControl fullWidth variant="outlined" size="small">
					<Select
						variant="outlined"
						value={conditionKind}
						onChange={event => handleChangeConditionKind(event.target.value as ContactBoardConditionKind)}
						fullWidth
					>
						{conditionKindOptions.map((kind) => (
							<MenuItem key={kind} value={kind}>
								{contactBoardSlugToContactBoardOptions[kind].label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			{!isValueMode && !isStatusMode && !isLossReasonMode && renderStageFields()}
			{isValueMode && renderValueFields()}
			{isStatusMode && renderStatusFields()}
			{isLossReasonMode && renderLossReasonFields()}
		</Grid>
	)
}

export default ContactBoardFilterCondition
